import { DoubleSide, LinearFilter, Mesh, Object3D, ShaderMaterial, UVMapping, } from "three";
import { CustomShader, ImageUtils, QuadtreeUtils } from "../../utils/internal";
import { isDefined, TILE_GEOMETRY } from "../../internal";
import { SceneGraphNode } from "../internal";
/**
 * A Image Tile of Quadtree-Image
 * @class Tile
 * @param quadcode
 * @param baseurl
 * @constructor
 * @param {string} quadCode
 * @param {string} baseUrl
 */
export class Tile extends SceneGraphNode {
    constructor(quadCode, baseUrl, tileWidth, imageWidth, imageHeight, traversalState, callback, stereoright) {
        super(null);
        /**
         * @property {number} tileWidth in pixel
         */
        this.tileWidth = 512;
        /**
         * container for 3d object
         * @default null
         */
        this.tMesh = null;
        this.nodata = false;
        /**
         * hold current texture Image
         * @property {Texture} _texture
         * @private
         * @default null
         */
        this._texture = null;
        this._gamma = 1.0;
        this._imageDataReadyCallback = null;
        this._pixelMap = null;
        /**
         * @property {boolean} ready is set to true if texture image is sucsessfully loaded
         */
        this.ready = false;
        /**
         * hidden 2d canvas for gamma correction
         * @type {null}
         */
        this.gammacorrectcanvas = null;
        /**
         * tile width in pixel -> is set after succesfully loading the image so it's the real image-width
         * @type {number}
         */
        this.width = 512;
        /**
         * tile width in pixel -> is set after succesfully loading the image so it's the real image-height
         * @type {number}
         */
        this.height = 512;
        this.oimage = null;
        this.imageurl = "";
        this.tileWidth = tileWidth;
        this.currentTraversalState = traversalState;
        if (isDefined(callback))
            this._imageDataReadyCallback = callback;
        this.quadCode = quadCode;
        this.lod = quadCode.length;
        //upper left corner of tile if whole image is [0...1][0...1]
        this.extent = QuadtreeUtils.QuadKeyToNormalizedCoord(quadCode);
        this._nx0 = this.extent[0] * this.tileWidth * 2;
        this._ny0 = this.extent[1] * this.tileWidth * 2;
        this._nx1 = this.extent[2] * this.tileWidth * 2; // lower left corner
        this._ny1 = this.extent[3] * this.tileWidth * 2;
        this._nscale = this.extent[2] - this.extent[0];
        this.tGeometry = TILE_GEOMETRY;
        const res = QuadtreeUtils.QuadKeyToTileCoord(this.quadCode);
        let image_url = baseUrl + "/0/?lod=" + res["lod"] + "&x=" + res["x"] + "&y=" + res["y"];
        if (stereoright)
            image_url = baseUrl + "/1/?lod=" + res["lod"] + "&x=" + res["x"] + "&y=" + res["y"];
        this.imageurl = image_url;
        if (this.extent[0] > imageWidth || this.extent[1] > imageHeight) {
            this.nodata = true;
            this.tMaterial = new ShaderMaterial();
        }
        else {
            const textureImage = traversalState.scene.engine.requestTexture(image_url, UVMapping, (texture) => {
                this._tileloadsuccess(texture);
            }, () => {
                this._tileloadfailed();
            }, false);
        }
    }
    _tileloadsuccess(texture) {
        this.tMaterial = new ShaderMaterial({
            transparent: true,
            uniforms: {
                uGamma: { value: this._gamma },
                uTexture: { value: texture },
                uOpacity: { value: 1.0 },
            },
            vertexShader: CustomShader.tileVertexShader,
            fragmentShader: CustomShader.tileFragmentShader,
            depthWrite: false,
            side: DoubleSide,
        });
        this.tMesh = new Mesh(this.tGeometry, this.tMaterial);
        this.tMesh.matrixAutoUpdate = true;
        this._zoom = 0;
        this.nodata = false;
        this._texture = texture;
        this._texture.minFilter = LinearFilter; //to prevent TextureNotPowerOfTwo warnings...
        this.ready = true;
        if (texture.image) {
            this.width = texture.image.width;
            this.height = texture.image.height;
        }
        if (this._imageDataReadyCallback != null)
            this._imageDataReadyCallback(true, texture);
    }
    _tileloadfailed() {
        this.nodata = true;
        this.ready = true;
        console.log("Load Failed");
        //just create an empty object to prevent adding or removing errors...
        this.tMesh = new Object3D();
        console.log("File Error: file not found: " + this.imageurl);
        if (this._imageDataReadyCallback != null) {
            this._imageDataReadyCallback(false, null);
        }
    }
    /**
     * Sets the tMesh.modelmatrix according to current pan and zoom values.
     *
     * reads:   traversalState.zoom
     *          traversalState.panX
     *          traversalState.panY
     *
     * @method render
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        super.render(traversalState);
        this._zoom = traversalState.zoom;
        const x = this._nx0 * traversalState.zoom + traversalState.panX;
        const y = this._ny0 * traversalState.zoom + traversalState.panY;
        const w = (this._nx1 - this._nx0) * traversalState.zoom + 0.5;
        const h = (this._ny1 - this._ny0) * traversalState.zoom + 0.5;
        this.tMesh.position.set(-traversalState.scene.engine.getContextWidth() / 2 + x, traversalState.scene.engine.getContextHeight() / 2 - y, 50);
        this.tMesh.scale.set(w, -h, 1);
        if (traversalState.gamma != this._gamma) {
            this._gamma = traversalState.gamma;
            //webgl
            const mat = this.tMaterial;
            mat.uniforms["uGamma"] = { value: this._gamma };
            mat.uniforms["uTexture"] = { value: this._texture };
        }
    }
    /**
     * Returns true if the tile data is fully loaded
     * @method isAvailable
     * @return {boolean} True if image is loaded
     */
    isAvailable() {
        if (this.nodata) {
            return true; // no data tiles are always available...
        }
        else if (!isDefined(this._texture) || this._texture == null) {
            return false;
        }
        return true;
    }
    updateGammaForCanvas() {
        if (this._texture == null)
            return; //image not loaded yet....
        if (this.oimage == null)
            this.oimage = this._texture.image;
        this._texture.image = ImageUtils.applyGammaCorrection(this.oimage, this._gamma);
    }
    /**
     * Sets the tiles image gamma value
     * @method setGamma
     * @param {number} value
     */
    setGamma(value) {
        this._gamma = value;
    }
    getPixelValue(x, y) {
        if (this.isAvailable()) {
            // calculate pixel coordinate
            // with of one pixel:
            const pixelwidth = this.tileWidth / (this.extent[2] - this.extent[0]);
            let posx = Math.floor(pixelwidth * (x - this.extent[0]) + 0.5);
            let posy = Math.floor(pixelwidth * (y - this.extent[1]) + 0.5);
            // clamp to [0,tilewidth-1]
            if (posx < 0)
                posx = 0;
            if (posx > this.tileWidth - 1)
                posx = this.tileWidth - 1;
            if (posy < 0)
                posy = 0;
            if (posy > this.tileWidth - 1)
                posy = this.tileWidth - 1;
            if (!isDefined(this._pixelMap) || this._pixelMap == null) {
                const img = this._texture.image;
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                canvas.getContext("2d").drawImage(img, 0, 0, img.width, img.height);
                this._pixelMap = canvas.getContext("2d").getImageData(0, 0, img.width, img.height).data;
            }
            return [
                this._pixelMap[posx * 4 + posy * this.tileWidth * 4] / 255.0,
                this._pixelMap[posx * 4 + posy * this.tileWidth * 4 + 1] / 255.0,
                this._pixelMap[posx * 4 + posy * this.tileWidth * 4 + 2] / 255.0,
                this._pixelMap[posx * 4 + posy * this.tileWidth * 4 + 3] / 255.0,
            ];
        }
        return [0.0, 0.0, 0.0, 0.0];
    }
    destroy() {
        try {
            this.tMaterial.uniforms.uTexture.value.dispose();
            this.tMaterial.dispose();
            this.tGeometry.dispose();
        }
        catch (e) { } // eslint-disable-line
    }
}
//# sourceMappingURL=tile.js.map